import { Service, ServiceStatus } from '../../types';

interface Props {
  services: Service[];
}

const tone: Record<ServiceStatus, { band: string; dot: string }> = {
  up:          { band: 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-900', dot: 'bg-status-up' },
  down:        { band: 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-900',         dot: 'bg-status-down' },
  degraded:    { band: 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-900', dot: 'bg-status-degraded' },
  pending:     { band: 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700',        dot: 'bg-status-pending' },
  maintenance: { band: 'bg-brand-50 dark:bg-brand-900/20 border-brand-200 dark:border-brand-900', dot: 'bg-status-maintenance' },
};

export function OverallStatus({ services }: Props) {
  const counts = services.reduce(
    (acc, s) => { acc[s.status] = (acc[s.status] || 0) + 1; return acc; },
    {} as Record<ServiceStatus, number>,
  );

  const total = services.length;
  const upCount = counts.up || 0;
  const downCount = counts.down || 0;
  const degradedCount = counts.degraded || 0;
  const maintenanceCount = counts.maintenance || 0;

  let status: ServiceStatus = 'up';
  let headline = 'All systems operational';

  if (total === 0) {
    status = 'pending';
    headline = 'No services configured';
  } else if (downCount > 0) {
    status = 'down';
    headline = `${downCount} service${downCount > 1 ? 's' : ''} down`;
  } else if (degradedCount > 0) {
    status = 'degraded';
    headline = `${degradedCount} service${degradedCount > 1 ? 's' : ''} degraded`;
  } else if (maintenanceCount > 0 && maintenanceCount === total) {
    status = 'maintenance';
    headline = 'All services under maintenance';
  }

  const { band, dot } = tone[status];

  return (
    <div className={`rounded-lg border p-5 ${band}`} role="status">
      <div className="flex items-center gap-4">
        <span className={`w-3 h-3 rounded-full shrink-0 ${dot}`} aria-hidden="true" />
        <div className="min-w-0">
          <h2 className="text-lg font-semibold tracking-tight">{headline}</h2>
          {total > 0 && (
            <p className="text-sm text-gray-600 dark:text-gray-400 tabular-nums">
              {upCount} of {total} operational
              {degradedCount > 0 && downCount > 0 && ` · ${degradedCount} degraded`}
              {maintenanceCount > 0 && ` · ${maintenanceCount} in maintenance`}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
